import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Phone, ShieldCheck, Eye, MessagesSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NC, telLink, openWhatsApp, defaultWhatsappMessage } from "@/lib/nc";

const trustPoints = [
  { icon: ShieldCheck, label: "Single-point responsibility" },
  { icon: Eye, label: "Active site supervision" },
  { icon: MessagesSquare, label: "Transparent communication" },
];

export const Hero = () => {
  return (
    <section id="home" className="relative overflow-hidden bg-gradient-hero">
      <div className="absolute inset-0 bg-grid-soft opacity-60" aria-hidden="true" />
      <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />

      <div className="container-nc relative grid gap-8 py-12 sm:gap-12 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:py-24">
        {/* Left: copy */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <span className="inline-flex max-w-full items-center justify-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gold whitespace-normal sm:px-4 sm:text-xs">
            Construction · Renovation · Repair · Maintenance
          </span>

          <h1 className="mt-5 font-heading text-[2rem] font-extrabold leading-[1.15] tracking-tight text-foreground text-balance sm:text-5xl lg:text-6xl">
            Construction with Clear Scope, Active Supervision and{" "}
            <span className="text-primary">Transparent Communication.</span>
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            From construction and renovation to repair and maintenance, NC provides
            single-point responsibility for your project in {NC.city}.
          </p>

          <div id="hero-actions" className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button asChild variant="cta" size="xl" className="w-full sm:w-auto" data-testid="hero-estimate-btn">
              <a href="#estimate">
                Request an Estimate <ArrowRight />
              </a>
            </Button>
            <Button
              variant="whatsapp"
              size="xl"
              className="w-full sm:w-auto"
              onClick={() => openWhatsApp(defaultWhatsappMessage)}
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle /> WhatsApp NC
            </Button>
            <Button asChild variant="outlineNavy" size="xl" className="w-full sm:w-auto" data-testid="hero-call-btn">
              <a href={telLink}>
                <Phone /> Call NC
              </a>
            </Button>
          </div>

          <dl className="mt-8 grid max-w-lg grid-cols-3 gap-3 border-t border-border pt-5 sm:gap-4">
            <div>
              <dt className="text-2xl font-extrabold text-primary sm:text-3xl">1</dt>
              <dd className="mt-1 text-xs leading-snug text-muted-foreground sm:text-sm">Accountable Partner</dd>
            </div>
            <div>
              <dt className="text-2xl font-extrabold text-primary sm:text-3xl">9+</dt>
              <dd className="mt-1 text-xs leading-snug text-muted-foreground sm:text-sm">Services Offered</dd>
            </div>
            <div>
              <dt className="text-2xl font-extrabold text-primary sm:text-3xl">Clear</dt>
              <dd className="mt-1 text-xs leading-snug text-muted-foreground sm:text-sm">Documented Scope</dd>
            </div>
          </dl>
        </motion.div>

        {/* Right: brand panel */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.12, ease: "easeOut" }}
          className="relative"
        >
          <div className="rounded-2xl border border-border bg-card p-6 shadow-elegant sm:p-8">
            <div className="flex justify-center rounded-xl bg-secondary/60 p-6 sm:p-8">
              <img
                src="/assets/nc-logo-trim.png"
                alt="NC – No Contractor. Honesty. Understanding. Responsibility."
                className="h-auto w-full max-w-[320px]"
              />
            </div>
            <p className="mt-6 text-center text-sm font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {NC.tagline}
            </p>
            <div className="mt-6 space-y-3">
              {trustPoints.map((point) => (
                <div
                  key={point.label}
                  className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <point.icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-semibold text-foreground">{point.label}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
